import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, FileText, Trash2, Printer, Banknote, CreditCard, X, Download } from "lucide-react";

const fmtRON = (v) => `${new Intl.NumberFormat("ro-RO", { minimumFractionDigits: 2 }).format(v)} RON`;
const emptyRow = () => ({ name: "", price: "", qty: 1, product_id: null });

export default function AdminInvoices() {
  const [invoices, setInvoices] = useState([]);
  const [products, setProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [client, setClient] = useState({ customer_name: "", customer_phone: "", customer_address: "", customer_cui: "" });
  const [method, setMethod] = useState("cash");
  const [rows, setRows] = useState([emptyRow()]);

  const load = async () => setInvoices((await api.get("/admin/invoices")).data);
  useEffect(() => {
    load();
    api.get("/admin/products").then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  const setRow = (i, k, v) => setRows(rows.map((r, idx) => idx === i ? { ...r, [k]: v } : r));
  const addFromProduct = (pid) => {
    const p = products.find((x) => x.id === pid);
    if (p) setRows([...rows.filter((r) => r.name || r.price), { name: p.name, price: p.price, qty: 1, product_id: p.id }]);
  };
  const total = rows.reduce((s, r) => s + (parseFloat(r.price) || 0) * (parseInt(r.qty, 10) || 0), 0);

  const save = async (e) => {
    e.preventDefault();
    const items = rows.filter((r) => r.name.trim() && parseFloat(r.price) > 0)
      .map((r) => ({ name: r.name.trim(), price: parseFloat(r.price), qty: parseInt(r.qty, 10) || 1, product_id: r.product_id || null }));
    if (!client.customer_name.trim()) { toast.error("Completeaza numele clientului"); return; }
    if (items.length === 0) { toast.error("Adauga cel putin un produs cu pret"); return; }
    try {
      const r = await api.post("/admin/invoices", { ...client, payment_method: method, items });
      toast.success(`Factura ${r.data.invoice_number} emisa!`);
      setOpen(false);
      setClient({ customer_name: "", customer_phone: "", customer_address: "", customer_cui: "" });
      setRows([emptyRow()]);
      load();
      window.open(`/admin/facturi/${r.data.id}/print`, "_blank");
    } catch (err) { toast.error(err?.response?.data?.detail || "Eroare la salvare"); }
  };

  const remove = async (inv) => {
    if (!confirm(`Stergi factura ${inv.invoice_number}? Stocul produselor din magazin se returneaza.`)) return;
    await api.delete(`/admin/invoices/${inv.id}`);
    toast.success("Factura stearsa");
    load();
  };

  const monthly = Object.values(invoices.reduce((acc, inv) => {
    const ym = (inv.created_at || "").slice(0, 7);
    const e = acc[ym] = acc[ym] || { month: ym, count: 0, total: 0 };
    e.count += 1; e.total += inv.total;
    return acc;
  }, {})).sort((a, b) => b.month.localeCompare(a.month)).slice(0, 6);

  const exportCSV = () => {
    const header = "Numar factura;Data;Client;Telefon;CUI;Produse;Metoda plata;Total (RON)";
    const lines = invoices.map((inv) => [
      inv.invoice_number,
      new Date(inv.created_at).toLocaleDateString("ro-RO"),
      inv.customer_name,
      inv.customer_phone || "",
      inv.customer_cui || "",
      inv.items.map((i) => `${i.qty} x ${i.name} (${i.price})`).join(" | "),
      inv.payment_method === "cash" ? "Numerar" : "Card",
      inv.total.toFixed(2).replace(".", ","),
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(";"));
    const blob = new Blob(["\uFEFF" + [header, ...lines].join("\n")], { type: "text/csv;charset=utf-8" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `facturi_trotix_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    toast.success("CSV descarcat — il deschizi in Excel");
  };

  return (
    <div data-testid="admin-invoices-page">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="font-display text-3xl uppercase">Facturi</h1>
          <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">Vanzari directe la service — emiti si printezi factura pe loc</div>
        </div>
        <div className="flex items-center gap-3">
          <Button data-testid="export-csv-btn" onClick={exportCSV} disabled={invoices.length === 0}
            className="bg-neutral-900 border border-[#00E5FF]/40 text-[#00E5FF] hover:bg-neutral-800 rounded-none uppercase tracking-wider font-bold text-xs">
            <Download className="w-4 h-4 mr-2" /> Export CSV
          </Button>
          <Button data-testid="new-invoice-btn" onClick={() => setOpen(true)} className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold">
            <Plus className="w-4 h-4 mr-2" /> Factura noua
          </Button>
        </div>
      </div>

      {monthly.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {monthly.map((m) => (
            <div key={m.month} data-testid={`invoice-month-${m.month}`} className="border border-neutral-800 bg-neutral-950 p-3">
              <div className="text-[10px] uppercase tracking-widest text-neutral-500">{m.month.slice(5)}/{m.month.slice(0, 4)}</div>
              <div className="text-lg font-bold text-[#00E5FF] mt-1">{fmtRON(m.total)}</div>
              <div className="text-[10px] text-neutral-500">{m.count} {m.count === 1 ? "factura" : "facturi"}</div>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {invoices.length === 0 && (
          <div className="border border-dashed border-neutral-800 p-14 text-center text-neutral-500">
            <FileText className="w-10 h-10 mx-auto mb-3 text-neutral-700" />
            Nicio factura emisa. Apasa "Factura noua" cand vinzi o trotineta la service.
          </div>
        )}
        {invoices.map((inv) => (
          <div key={inv.id} data-testid={`invoice-row-${inv.invoice_number}`} className="border border-neutral-800 bg-neutral-950 p-4 flex flex-wrap items-center gap-4">
            <div className="font-mono font-bold text-[#00E5FF] min-w-[100px]">{inv.invoice_number}</div>
            <div className="flex-1 min-w-[180px]">
              <div className="font-semibold text-sm">{inv.customer_name}</div>
              <div className="text-xs text-neutral-500">{inv.items.map((i) => `${i.qty} × ${i.name}`).join(", ")}</div>
            </div>
            <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-wider bg-neutral-900 border border-neutral-800 px-2 py-1">
              {inv.payment_method === "cash" ? <Banknote className="w-3 h-3" /> : <CreditCard className="w-3 h-3" />}
              {inv.payment_method === "cash" ? "Numerar" : "Card"}
            </span>
            <div className="text-right min-w-[110px]">
              <div className="font-bold text-[#00E5FF]">{fmtRON(inv.total)}</div>
              <div className="text-[10px] text-neutral-600">{new Date(inv.created_at).toLocaleDateString("ro-RO")}</div>
            </div>
            <button data-testid={`print-invoice-${inv.invoice_number}`} onClick={() => window.open(`/admin/facturi/${inv.id}/print`, "_blank")}
              className="p-2 text-neutral-400 hover:text-[#00E5FF]" title="Printeaza"><Printer className="w-4 h-4" /></button>
            <button data-testid={`delete-invoice-${inv.invoice_number}`} onClick={() => remove(inv)} className="p-2 text-neutral-500 hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-neutral-950 border-neutral-800 text-white max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-display uppercase">Factura noua · vanzare directa</DialogTitle></DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Nume client *</Label>
                <Input data-testid="invoice-name-input" value={client.customer_name} onChange={(e) => setClient({ ...client, customer_name: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Telefon</Label>
                <Input data-testid="invoice-phone-input" value={client.customer_phone} onChange={(e) => setClient({ ...client, customer_phone: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">Adresa (optional)</Label>
                <Input data-testid="invoice-address-input" value={client.customer_address} onChange={(e) => setClient({ ...client, customer_address: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-neutral-400">CUI / CNP firma (optional)</Label>
                <Input data-testid="invoice-cui-input" value={client.customer_cui} onChange={(e) => setClient({ ...client, customer_cui: e.target.value })} className="bg-black border-neutral-800 rounded-none mt-1" />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-xs uppercase tracking-wider text-[#00E5FF]">Produse pe factura</Label>
                <Select value="" onValueChange={addFromProduct}>
                  <SelectTrigger data-testid="invoice-product-select" className="bg-black border-neutral-800 rounded-none w-56 h-8 text-xs"><SelectValue placeholder="+ Adauga din magazin" /></SelectTrigger>
                  <SelectContent className="bg-neutral-950 border-neutral-800 text-white">
                    {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} — {p.price} RON</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                {rows.map((r, i) => (
                  <div key={i} className="flex gap-2 items-center">
                    <Input data-testid={`invoice-item-name-${i}`} value={r.name} onChange={(e) => setRow(i, "name", e.target.value)} placeholder="Denumire (ex: Trotineta isinwheel S Nova)" className="bg-black border-neutral-800 rounded-none flex-1" />
                    <Input data-testid={`invoice-item-price-${i}`} type="number" step="0.01" value={r.price} onChange={(e) => setRow(i, "price", e.target.value)} placeholder="Pret" className="bg-black border-neutral-800 rounded-none w-28" />
                    <Input data-testid={`invoice-item-qty-${i}`} type="number" min="1" value={r.qty} onChange={(e) => setRow(i, "qty", e.target.value)} className="bg-black border-neutral-800 rounded-none w-16" />
                    <button type="button" onClick={() => setRows(rows.length > 1 ? rows.filter((_, idx) => idx !== i) : [emptyRow()])} className="p-2 text-neutral-500 hover:text-red-500"><X className="w-4 h-4" /></button>
                  </div>
                ))}
              </div>
              <button type="button" data-testid="invoice-add-row-btn" onClick={() => setRows([...rows, emptyRow()])}
                className="mt-2 text-xs uppercase tracking-wider text-[#00E5FF] hover:text-white transition-colors">+ Rand nou</button>
            </div>

            <div className="flex items-center justify-between gap-4">
              <div className="flex gap-2">
                {[["cash", "Numerar", Banknote], ["card", "Card", CreditCard]].map(([v, l, Icon]) => (
                  <button key={v} type="button" data-testid={`invoice-pay-${v}`} onClick={() => setMethod(v)}
                    className={`inline-flex items-center gap-2 border px-4 py-2 text-xs uppercase tracking-wider font-bold transition-colors ${method === v ? "border-[#00E5FF] bg-[#00E5FF]/10 text-[#00E5FF]" : "border-neutral-800 text-neutral-400 hover:border-neutral-600"}`}>
                    <Icon className="w-4 h-4" /> {l}
                  </button>
                ))}
              </div>
              <div className="text-right">
                <div className="text-[10px] uppercase tracking-widest text-neutral-500">Total</div>
                <div data-testid="invoice-total" className="text-2xl font-bold text-[#00E5FF]">{fmtRON(total)}</div>
              </div>
            </div>

            <DialogFooter>
              <Button data-testid="invoice-save-btn" type="submit" className="bg-[#00E5FF] text-black hover:bg-[#5CEEFF] rounded-none uppercase tracking-wider font-bold w-full">
                <Printer className="w-4 h-4 mr-2" /> Emite si printeaza factura
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
